/********************************************************************************************************************
 * Purpose: Hook to manage user merge requests for a company. Returns functions and data to handle adding, deleting,
 * and updating requests. 🐺
 * **************************
 *
 * @format
 */

import {Table, Toast} from '@sttark/design-system';
import {IfElse} from 'frontier-utilities';
import {useState, useEffect} from 'react';
import SuperAgent from 'superagent';
import React from 'react';

export const RequestsList = props => {
    const [isResending, setIsResending] = useState('');
    const [isDeleting, setIsDeleting] = useState('');
    const [requests, setRequests] = useState([]);
    const [toast, setToast] = useState({
        isDanger: false,
        isOpen: false,
        message: ''
    });

    function handleResendRequest(requestId) {
        setIsResending(requestId);

        SuperAgent.post('/account/user-merge-requests/re-send/' + requestId)
            .set('Accept', 'application/json')
            .send({foo: 'bar'})
            .end((err, response) => {
                if (response.body && response.body.request) {
                    setRequests(prevRequests => {
                        const updatedRequests = [...prevRequests];
                        const request = response.body.request;
                        const requestIdx = updatedRequests.findIndex(r => r.id === request.id);

                        updatedRequests[requestIdx] = request;
                        return updatedRequests;
                    });
                    setToast({
                        isDanger: false,
                        isOpen: true,
                        message: 'Request sent successfully.'
                    });
                } else if (err) {
                    setToast({
                        isDanger: true,
                        isOpen: true,
                        message: 'Error re-sending request.'
                    });
                    setIsDeleting('');
                }
                setIsResending(false);
            });
    }

    function handleDeleteRequest(requestId) {
        setIsDeleting(requestId);

        SuperAgent.delete('/account/user-merge-requests/delete/' + requestId)
            .set('Accept', 'application/json')
            .end((err, response) => {
                if (response.body && response.body.success) {
                    setRequests(prevRequests => {
                        const updatedRequests = [...prevRequests];
                        const requestIdx = updatedRequests.findIndex(
                            request => request.id === requestId
                        );
                        updatedRequests.splice(requestIdx, 1);
                        return updatedRequests;
                    });

                    setToast({
                        isDanger: false,
                        isOpen: true,
                        message: 'Request deleted successfully.'
                    });
                } else {
                    setToast({
                        isDanger: true,
                        isOpen: true,
                        message: 'Error deleting request.'
                    });
                }
                setIsDeleting('');
            });
    }

    const hideToast = () => {
        setToast({
            isDanger: false,
            isOpen: false,
            message: ''
        });
    };

    useEffect(() => {
        setRequests(props.users);
    }, [props.users]);

    if (!requests.length) {
        return (
            <aside className="alert alert-info mt-4" role="alert">
                You don't have any pending requests.
            </aside>
        );
    }

    return (
        <>
            <Toast
                danger={toast.isDanger}
                autoHideDuration={4000}
                isOpen={toast.isOpen}
                onClose={hideToast}
                zIndex={3001}
                autoHide>
                <p className="color-structure-white m-0">{toast.message}</p>
            </Toast>

            <Table
                columns={[
                    {key: 'user_name', title: 'User Name', index: 'user_name'},
                    {key: 'email', title: 'Email', index: 'email'},
                    {key: 'status', title: 'Status', index: 'status'},
                    {key: 'id', title: 'ID', index: 'id'},
                    {key: 'actions', title: '', index: 'actions'}
                ]}
                rows={requests.map(request => ({
                    actions: (
                        <div className="d-flex align-items-center justify-content-end">
                            <div>
                                <IfElse condition={isResending === request.id}>
                                    <button className="color-structure-darker" disabled="disabled">
                                        <i className="fa fa-spinner fa-spin" />
                                        <u>Re-sending...</u>
                                    </button>
                                    <button
                                        className="p-0 m-0 color-accent-primary d-flex align-items-center justify-content-start"
                                        onClick={() => handleResendRequest(request.id)}>
                                        <i className="fa fa-send me-2" />
                                        <u>Re-send</u>
                                    </button>
                                </IfElse>
                                <IfElse condition={isDeleting === request.id}>
                                    <button className="color-structure-darker" disabled="disabled">
                                        <i className="fa fa-spinner fa-spin" />
                                        <u>Deleting...</u>
                                    </button>
                                    <button
                                        className="p-0 m-0 color-alert-negative d-flex align-items-center justify-content-start"
                                        onClick={() => handleDeleteRequest(request.id)}>
                                        <i className="fa fa-trash me-2" />
                                        <u>Delete</u>
                                    </button>
                                </IfElse>
                            </div>
                        </div>
                    ),
                    status: (
                        <IfElse condition={request.is_expired}>
                            <div className="p-0 m-0 d-flex align-items-center justify-content-start">
                                <i className="fa fa-exclamation-triangle me-2 color-alert-negative" />
                                <p className="m-0 p-0 color-alert-negative">
                                    Expired, please re-send
                                </p>
                            </div>
                            <div className="p-0 m-0 color-accent-tertiary d-flex align-items-center justify-content-start">
                                <i className="fa fa-warning me-2" style={{color: '#e5b200'}} />
                                <p className="m-0 p-0" style={{color: '#e5b200'}}>
                                    Waiting on user
                                </p>
                            </div>
                        </IfElse>
                    ),
                    user_name: request.user.name,
                    email: request.user.email,
                    id: request.user.id,
                    key: request.id
                }))}
            />
        </>
    );
};
